import { FormManager } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';

import { ErrorBag } from '../../../common/helpers';
import { TranslatableInput } from '../../Translations/Inputs';
import { Translation } from '../../Translations/helpers';
import GetEventsForMailPermissionsQuery from './GetEventsForMailPermissionsQuery';
import UI from '../../../common/UI';
import useQuery from '../../../api/useQuery';

export interface MailPermissionData {
  title: string;
  title_translations: Translation[];
  label: string;
  label_translations: Translation[];
  events: string[];
}

interface MailPermission {
  id: string;
  interest_list_event?: { id: string } | null;
}

interface MailPermissionFormProps {
  form: FormManager<MailPermissionData>;
  errors: ErrorBag;
  mailPermission?: MailPermission;
  project: {
    id: string,
    locale: Locale,
  };
}

const MailPermissionForm = ({ form, errors, mailPermission, project }: MailPermissionFormProps) => {
  const { t } = useTranslation();

  const { data } = useQuery(
    GetEventsForMailPermissionsQuery, {
      variables: {
        id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const events = data?.project.events.data || [];

  if (!data) {
    return <UI.PageLoader />;
  }

  const isInterestList = !!mailPermission?.interest_list_event;

  return (
    <UI.FormGrid>
      <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
        <UI.InputLabel htmlFor="MailPermission_Title">
          {t('title')}
          {' '}
          <UI.RequiredMark />
        </UI.InputLabel>
        <UI.InputDescription>
          {t('mail_permission_title_description')}
        </UI.InputDescription>
        <TranslatableInput
          form={form}
          errors={errors}
          name="title"
          defaultLocale={project.locale}
          title={t('title')}
          maxLength={255}
          placeholder={t('mail_permission_title_placeholder')}
          id="MailPermission_Title"
          autoFocus={!mailPermission}
        />
        <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
      </UI.InputGroup>

      <UI.InputGroup sc={{ valid: !errors.label, touched: form.touched.label }}>
        <UI.InputLabel htmlFor="MailPermission_Label">
          {t('opt_in_label')}
          {' '}
          <UI.RequiredMark />
        </UI.InputLabel>
        <TranslatableInput
          form={form}
          errors={errors}
          name="label"
          defaultLocale={project.locale}
          title={t('opt_in_label')}
          maxLength={255}
          placeholder={t('mail_permission_label_placeholder')}
          id="MailPermission_Label"
        />
        <UI.ErrorMessages attribute={t('label')} errors={errors.label} />
      </UI.InputGroup>

      {!isInterestList && (
        <UI.InputGroup sc={{ valid: !errors.events, touched: form.touched.events }}>
          <UI.InputLabel>
            {t('collect_opt_ins_for')}
          </UI.InputLabel>
          {events.map((event) => (
            <UI.Checkbox
              {...form.checklist('events', event.id)}
              key={event.id}
            >
              {event.title}
            </UI.Checkbox>
          ))}
        </UI.InputGroup>
      )}
    </UI.FormGrid>
  );
};

export default MailPermissionForm;
